import React from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { GapStylingConfig } from './types';

/**
 * Utility functions for generating CSS styles and backgrounds
 */
const generatePatternBackground = (config: GapStylingConfig): string => {
  const { patternType, patternColor, patternSecondaryColor, patternSize } = config;

  switch (patternType) {
    case 'dots':
      return `radial-gradient(circle at ${patternSize}px ${patternSize}px, ${patternColor} 2px, ${patternSecondaryColor} 2px)`;
    case 'stripes':
      return `repeating-linear-gradient(45deg, ${patternColor}, ${patternColor} ${patternSize}px, ${patternSecondaryColor} ${patternSize}px, ${patternSecondaryColor} ${patternSize * 2}px)`;
    case 'waves':
      return `radial-gradient(ellipse at top, ${patternColor}, transparent), radial-gradient(ellipse at bottom, ${patternSecondaryColor}, transparent)`;
    case 'checkerboard':
      return `linear-gradient(45deg, ${patternColor} 25%, transparent 25%, transparent 75%, ${patternColor} 75%), linear-gradient(45deg, ${patternColor} 25%, transparent 25%, transparent 75%, ${patternColor} 75%)`;
    case 'zigzag':
      return `linear-gradient(135deg, ${patternColor} 25%, transparent 25%) -${patternSize}px 0, linear-gradient(225deg, ${patternColor} 25%, transparent 25%) -${patternSize}px 0, linear-gradient(315deg, ${patternColor} 25%, transparent 25%), linear-gradient(45deg, ${patternColor} 25%, transparent 25%)`;
    default:
      return patternColor;
  }
};

const generateGradient = (config: GapStylingConfig): string => {
  const { gradientType, gradientDirection, gradientColors } = config;
  const colorStops = gradientColors
    .sort((a, b) => a.position - b.position)
    .map(color => `${color.color} ${color.position}%`)
    .join(', ');

  switch (gradientType) {
    case 'linear':
      return `linear-gradient(${gradientDirection}, ${colorStops})`;
    case 'radial':
      return `radial-gradient(circle, ${colorStops})`;
    case 'conic':
      return `conic-gradient(from ${gradientDirection}, ${colorStops})`;
    default:
      return `linear-gradient(${gradientDirection}, ${colorStops})`;
  }
};

const getPreviewStyle = (config: GapStylingConfig): React.CSSProperties => {
  if (!config.enabled) return { display: 'none' };

  let background = '';
  switch (config.backgroundType) {
    case 'color':
      background = config.backgroundColor;
      break;
    case 'gradient':
      background = generateGradient(config);
      break;
    case 'image':
      background = config.backgroundImage
        ? `url(${config.backgroundImage})`
        : config.backgroundColor;
      break;
    case 'pattern':
      background = generatePatternBackground(config);
      break;
  }

  const boxShadowValue = config.boxShadow.enabled
    ? `${config.boxShadow.inset ? 'inset ' : ''}${config.boxShadow.x}px ${config.boxShadow.y}px ${config.boxShadow.blur}px ${config.boxShadow.spread}px ${config.boxShadow.color}`
    : 'none';

  const animationValue = config.animation.enabled && config.animation.type !== 'none'
    ? `gap-animation-${config.animation.type} ${config.animation.duration}s ${config.animation.infinite ? 'infinite' : ''}`
    : 'none';

  return {
    height: `${config.height}px`,
    background,
    backgroundRepeat: config.backgroundType === 'image' ? config.imageRepeat : undefined,
    backgroundSize: config.backgroundType === 'image'
      ? config.imageSize === 'custom'
        ? `${config.imageSizeWidth} ${config.imageSizeHeight}`
        : config.imageSize
      : undefined,
    backgroundPosition: config.backgroundType === 'image' ? config.imagePosition : undefined,
    opacity: config.opacity,
    borderRadius: `${config.borderRadius}px`,
    border: config.borderWidth > 0
      ? `${config.borderWidth}px ${config.borderStyle} ${config.borderColor}`
      : 'none',
    boxShadow: boxShadowValue,
    margin: `${config.margin.top}px ${config.margin.right}px ${config.margin.bottom}px ${config.margin.left}px`,
    mixBlendMode: config.blendMode as React.CSSProperties['mixBlendMode'],
    animation: animationValue,
  };
};

interface PreviewSectionProps {
  config: GapStylingConfig;
}

/**
 * Preview section component for gap styling configuration
 * Shows a live preview of the gap styling between mock raffle items
 */
const PreviewSection: React.FC<PreviewSectionProps> = ({ config }) => {
  if (!config.enabled) return null;

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>
        Preview
      </Typography>
      <Paper
        sx={{
          p: 2,
          backgroundColor: '#f9f9f9',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ mb: 1, p: 1, border: '1px dashed #ccc' }}>
          Raffle Item Above
        </Box>

        <Box sx={getPreviewStyle(config)} />

        <Box sx={{ mt: 1, p: 1, border: '1px dashed #ccc' }}>
          Raffle Item Below
        </Box>
      </Paper>
    </Box>
  );
};

export default PreviewSection;